<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'
import { FALLBACK_DOWNLOAD_LINKS } from '~/api/download'
const isChinese = computed(() => isChineseLocale(locale))

const PHONE_FRAME = {
  'zh-cn': 'https://resources1.interface003.com/web30/2026/09/04/5f14e2de-68ac-48cd-8b1b-3a29fbdc3617.png',
  zh: 'https://resources1.interface003.com/web30/2026/09/04/5b139530-072b-4001-a33d-5c8e35ce1380.png',
  other: 'https://resources1.interface003.com/web30/2026/09/04/0cc914e7-16c1-4d29-b8ec-acfa97833591.png',
}

const backgroundImage = 'https://resources1.interface003.com/web30/2026/09/04/873bfd55-53d7-4452-affa-a9fd95e889fa.jpg'
const CLOSE_ICON = 'https://resources1.interface003.com/web30/2026/09/04/c6d9ac10-d27e-4722-a6d7-c15047969a5e.png'
const SUCCESS_ICON = 'https://resources1.interface003.com/web30/2026/09/04/285c9899-ab92-4cf4-81ac-13b8fa9f6eb7.png'
const { t } = useI18n()
const { locale, localePath } = useAppI18n()
const { data: downloadAppUrls } = await useAppDownloadLinks()

const props = defineProps<{
  visible: boolean
  title?: string
}>()

const titleRef = ref<HTMLElement | null>(null)
/** 右侧宣传图距离弹窗顶部的偏移，跟随标题实际高度更新。 */
const phoneImageTop = ref(74)
let titleResizeObserver: ResizeObserver | null = null

/** 二维码与下载页共用下载落地地址，保证两个入口扫码结果一致。 */
const downloadQrUrl = computed(() => downloadAppUrls.value?.h5down || FALLBACK_DOWNLOAD_LINKS.h5down)

/** 根据当前站点语言选择对应文案的手机视觉素材。 */
const phoneFrameImage = computed(() => {
  const currentLocale = String(locale).toLowerCase()

  if (currentLocale === 'zh-cn') {
    return PHONE_FRAME['zh-cn']
  }

  if (['zh', 'zh-tw', 'zh-hk'].includes(currentLocale)) {
    return PHONE_FRAME.zh
  }

  return PHONE_FRAME.other
})

const emit = defineEmits<{
  close: []
}>()

/** 关闭弹窗并恢复页面滚动。 */
const closeDialog = () => {
  emit('close')
}

/** 根据弹窗状态锁定背景页面滚动。 */
const updateBodyOverflow = (visible: boolean) => {
  if (!import.meta.client) {
    return
  }

  document.body.style.overflow = visible ? 'hidden' : ''
}

/** 根据标题高度让右侧宣传图保持在标题分隔线前 5px 的位置。 */
const updatePhoneImageTop = () => {
  const titleElement = titleRef.value

  if (!titleElement) {
    return
  }

  phoneImageTop.value = 32 + titleElement.offsetHeight + 16 - 5
}

/** 监听标题尺寸变化，覆盖弹窗首次打开和语言切换后的重新排版。 */
const observeTitleSize = () => {
  if (titleResizeObserver || typeof ResizeObserver === 'undefined' || !titleRef.value) {
    return
  }

  titleResizeObserver = new ResizeObserver(updatePhoneImageTop)
  titleResizeObserver.observe(titleRef.value)
}

watch(
  () => props.visible,
  (visible) => {
    updateBodyOverflow(visible)

    if (visible) {
      void nextTick(() => {
        updatePhoneImageTop()
        observeTitleSize()
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  void nextTick(() => {
    updatePhoneImageTop()
    observeTitleSize()
  })
})

onBeforeUnmount(() => {
  updateBodyOverflow(false)
  titleResizeObserver?.disconnect()
  titleResizeObserver = null
})
</script>

<template>
  <Teleport to="body">
    <Transition name="related-download-dialog">
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(0,0,0,0.55)] p-[16px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="related-download-dialog-title"
        @click.self="closeDialog"
      >
        <div
          class="related-download-dialog relative h-auto w-[720px] overflow-hidden rounded-[28px]"
          :class="isChinese ? 'min-h-[404px]' : 'min-h-[430px]'"
        >
          <CommonImage
            :src="backgroundImage"
            alt=""
            width="720"
            loading="eager"
            :fallback-src="false"
            class="absolute inset-0 z-0 size-full object-cover"
            aria-hidden="true"
          />

          <div class="relative z-10 mx-[32px] flex flex-col pt-[32px] pb-[32px]">
            <div class="flex items-start gap-[8px] border-b border-[rgba(227,228,242,0.12)] pb-[16px]">
              <h2
                id="related-download-dialog-title"
                ref="titleRef"
                class="min-w-0 flex-1 text-[24px] leading-[32px] font-[600] break-words whitespace-normal text-white"
              >
                {{ t('82528') }}
              </h2>
              <button
                type="button"
                class="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors"
                aria-label="Close"
                @click="closeDialog"
              >
                <CommonImage :src="CLOSE_ICON" alt="" class="size-[32px]" loading="eager" :fallback-src="false" />
              </button>
            </div>

            <div class="mt-[24px] flex w-[392px] flex-col gap-[24px] rtl:mr-0 rtl:ml-auto">
              <div class="flex flex-col gap-[16px]">
                <p class="m-0 w-[350px] text-[18px] leading-[26px] font-[600] text-white">
                  <span>{{ t('88833') }}</span>
                  <span class="px-[3px] text-[#CFE1FD]">{{ props.title }}</span>
                </p>
                <div class="flex flex-col gap-[8px] text-[14px] leading-[22px] text-white">
                  <p class="m-0 flex items-start gap-[8px]">
                    <CommonImage
                      :src="SUCCESS_ICON"
                      alt=""
                      class="mt-[3px] size-[16px] shrink-0"
                      loading="eager"
                      :fallback-src="false"
                    />
                    <span>{{ t('88835') }}</span>
                  </p>
                  <p class="m-0 flex items-start gap-[8px]">
                    <CommonImage
                      :src="SUCCESS_ICON"
                      alt=""
                      class="mt-[3px] size-[16px] shrink-0"
                      loading="eager"
                      :fallback-src="false"
                    />
                    <span>{{ t('88836') }}</span>
                  </p>
                </div>
              </div>

              <div class="group flex w-fit flex-col items-start gap-[8px]">
                <span
                  class="flex size-[120px] items-center justify-center rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-white p-[8px]"
                >
                  <span class="relative size-[104px]">
                    <CommonQrCode
                      v-if="downloadQrUrl"
                      :text="downloadQrUrl"
                      :size="104"
                      dark-color="#354D88"
                      class="size-full"
                    />
                    <span v-if="downloadQrUrl" class="absolute inset-0 m-auto size-[26px]">
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/08/03/390d0b89-176a-419d-a4c7-e9ecd8b6e47c.png"
                        alt="WikiBit"
                        class="size-full"
                        loading="eager"
                        :fallback-src="false"
                      />
                    </span>
                  </span>
                </span>
                <span class="mx-auto flex items-center gap-[8px] text-[14px] leading-[22px] text-[#B7B9BE]">
                  {{ t('88837') }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="pointer-events-none absolute right-[9px] bottom-[5px] z-10 w-[328px] overflow-hidden rtl:right-auto rtl:left-[9px]"
            :style="{ top: `${phoneImageTop}px` }"
          >
            <CommonImage
              :src="phoneFrameImage"
              alt=""
              width="328"
              height="auto"
              class="size-full object-contain"
              loading="eager"
              :fallback-src="false"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.related-download-dialog-enter-active,
.related-download-dialog-leave-active {
  transition: opacity 180ms ease;
}

.related-download-dialog-enter-from,
.related-download-dialog-leave-to {
  opacity: 0;
}
</style>
